import { useTranslation } from "react-i18next";
import { formatDate } from "@serie3/common/Shared/timeingStuff";
import listHandler from "@serie3/common/Domain/listHandler";
import crudApi from "@serie3/common/api/crudApi";
import SocialEndpoints from "@serie3/mymediaplanner/Modules/Social/endpoints";
import Interactions from "../../../../shared/Interactions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faStar, faGaugeHigh } from "@fortawesome/free-solid-svg-icons";

const STATUS_STYLES = {
  draft:       { bg: "#f5f6f8", color: "#74788d" },
  in_progress: { bg: "rgba(3,142,220,0.12)", color: "#038edc" },
  ready:       { bg: "rgba(81,210,140,0.12)", color: "#2e9e65" },
  published:   { bg: "rgba(81,210,140,0.15)", color: "#1a7a4a" },
  planned:     { bg: "rgba(86,74,177,0.12)", color: "#564ab1" },
};

const Slider = ({
  post,
  setEditPost,
  setShowEdit,
  posts,
  setPosts,
  index,
  accentColor = "#038edc",
  averageScore = 0,
}) => {
  const { t } = useTranslation();
  const score = Number(post.stats?.at(-1)?.score) || 0;

  const pct = averageScore > 0 ? (score - averageScore) / averageScore : 0;
  const starCount = pct >= 0.30 ? 3 : pct >= 0.20 ? 2 : pct >= 0.10 ? 1 : 0;
  const scoreColor = pct <= -0.10 ? "#f34e4e" : pct >= 0.10 ? "#51d28c" : "#f0a500";
  const statusStyle = STATUS_STYLES[post.status] ?? STATUS_STYLES.draft;

  return (
    <div
      onClick={() => { setEditPost(post); setShowEdit(true); }}
      style={{
        background: "#ffffff",
        border: "1px solid #eef0f3",
        borderTop: `3px solid ${accentColor}`,
        borderRadius: 12,
        padding: "14px 16px",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        cursor: "pointer",
        transition: "box-shadow 0.15s ease, border-color 0.15s ease",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.08)"; e.currentTarget.style.borderColor = accentColor + "60"; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = "#eef0f3"; }}
    >
      {/* Title row */}
      <div className="d-flex align-items-start justify-content-between gap-2">
        <h5 style={{ fontSize: 15, fontWeight: 400, color: "#1a2236", margin: 0, lineHeight: 1.35, flex: 1 }}>
          {post.title}
        </h5>
        {starCount > 0 && (
          <span style={{ display: "inline-flex", gap: 2, flexShrink: 0, alignItems: "center" }}>
            {Array.from({ length: starCount }).map((_, i) => (
              <FontAwesomeIcon key={i} icon={faStar} style={{ fontSize: 11, color: "#f7cc53" }} />
            ))}
          </span>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            listHandler.deleteFromList(
              index,
              crudApi.del(SocialEndpoints.posts.contentPillars.deleteFromPillar, post.id),
              setPosts,
              posts
            );
          }}
          title={t("Backend.Post.ContentPillars.RemoveFromPillar")}
          style={{ background: "none", border: "none", padding: "2px 4px", cursor: "pointer", color: "#ff2d55", flexShrink: 0, lineHeight: 1, transition: "color 0.15s ease" }}
          onMouseEnter={(e) => { e.currentTarget.style.color = "#c0001a"; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = "#ff2d55"; }}
        >
          <FontAwesomeIcon icon={faTrash} style={{ fontSize: 13 }} />
        </button>
      </div>

      {/* Date */}
      <p style={{ fontSize: 11, color: "#adb5bd", margin: 0 }}>{formatDate(post.last_edit)}</p>

      {/* Text preview */}
      <p className="truncate-two-lines" style={{ fontSize: 12, color: "#74788d", margin: 0, flex: 1 }}>
        {post.text}
      </p>

      {/* Interactions + Score */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4 }}>
        <Interactions
          views={post.stats?.at(-1)?.views ?? 0}
          likes={post.stats?.at(-1)?.likes ?? 0}
          comments={post.stats?.at(-1)?.comments ?? 0}
        />
        {score > 0 && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700, color: scoreColor, flexShrink: 0 }}>
            <FontAwesomeIcon icon={faGaugeHigh} style={{ fontSize: 11 }} />
            {Math.round(score)}
          </span>
        )}
      </div>

      {/* Badges */}
      <div className="d-flex flex-wrap gap-1" style={{ marginTop: 4 }}>
        {post.content_type && (
          <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 20, background: "rgba(3,142,220,0.1)", color: "#038edc", letterSpacing: "0.3px" }}>
            {post.content_type}
          </span>
        )}
        {post.post_type && (
          <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 20, background: "rgba(95,208,243,0.12)", color: "#0e9bbf", letterSpacing: "0.3px" }}>
            {post.post_type}
          </span>
        )}
        {post.status && (
          <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 20, background: statusStyle.bg, color: statusStyle.color, letterSpacing: "0.3px" }}>
            {post.status}
          </span>
        )}
      </div>
    </div>
  );
};

export default Slider;
